import React, { useId, useState } from 'react';
import {
  Line,
  Area,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

export interface DataPoint {
  date: string;
  value: number;
  change?: number;
  changePercent?: number;
}

interface LineChartProps {
  data: DataPoint[];
  height?: number;
  showGrid?: boolean;
  showTooltip?: boolean;
  color?: string;
  areaColor?: string;
  strokeWidth?: number;
  className?: string;
}

const PortfolioLineChart: React.FC<LineChartProps> = ({
  data,
  height = 300,
  showGrid = false,
  showTooltip = true,
  color = '#00D4AA',
  areaColor = 'rgba(0, 212, 170, 0.1)',
  strokeWidth = 2,
  className = '',
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const gradientId = useId();

  const formatValue = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  interface CustomTooltipProps {
    active?: boolean;
    label?: string | number;
    payload?: Array<{ payload: DataPoint }> | null;
  }

  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      const dateLabel = typeof label === 'string' ? label : typeof label === 'number' ? String(label) : dataPoint.date;
      return (
        <div className="bg-gray-900 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-gray-300 text-sm">{formatDate(dateLabel)}</p>
          <p className="text-white font-semibold text-lg">
            {formatValue(dataPoint.value)}
          </p>
          {dataPoint.change !== undefined && (
            <p className={`text-sm ${dataPoint.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {dataPoint.change >= 0 ? '+' : ''}{formatValue(dataPoint.change)}
              {typeof dataPoint.changePercent === 'number'
                ? ` (${dataPoint.changePercent >= 0 ? '+' : ''}${dataPoint.changePercent.toFixed(2)}%)`
                : ''}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  interface CustomDotProps {
    cx?: number;
    cy?: number;
    index?: number;
  }

  const CustomDot: React.FC<CustomDotProps> = (props) => {
    const { cx, cy, index } = props;
    const isActive = activeIndex === index;
    
    return (
      <g>
        {isActive && (
          <circle
            cx={cx}
            cy={cy}
            r={6}
            fill="rgba(0, 212, 170, 0.3)"
            stroke={color}
            strokeWidth={2}
          />
        )}
        <circle
          cx={cx}
          cy={cy}
          r={isActive ? 4 : 0}
          fill={color}
          className="transition-all duration-200"
        />
      </g>
    );
  };

  return (
    <div className={`relative ${className}`}>
      <ResponsiveContainer width="100%" height={height}>
          <AreaChart
            data={data}
            margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
            onMouseMove={(chartState) => {
              if (chartState && typeof chartState.activeTooltipIndex === 'number') {
                setActiveIndex(chartState.activeTooltipIndex);
              }
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
          {showGrid && (
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#374151"
              opacity={0.3}
            />
          )}
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={areaColor} stopOpacity={0.8} />
              <stop offset="95%" stopColor={areaColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#9CA3AF', fontSize: 12 }}
            tickFormatter={formatDate}
            hide={true}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: '#9CA3AF', fontSize: 12 }}
            tickFormatter={formatValue}
            hide={true}
          />
          <Area
            type="monotone"
            dataKey="value"
            stroke="none"
            fill={`url(#${gradientId})`}
            fillOpacity={0.3}
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={strokeWidth}
            dot={false}
            activeDot={<CustomDot />}
            connectNulls={true}
          />
          {showTooltip && (
            <Tooltip
              content={<CustomTooltip />}
              cursor={false}
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PortfolioLineChart; 